import React from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  AccountButton,
  SwitchWorkspaceModal,
  FeedbackButton,
  useAppContext,
  useModal,
} from "@examspark/common-app";
import { Button, HSpace, RouterLink } from "@examspark/common-ui";
import {
  IconCampusLine,
  IconQuestionCircleLine,
} from "@examspark/common-ui/dist/components/icons";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";

import { useFlags } from "../../hooks/useFlags";
import logo from "../../images/tcmpc-logo-header.png";

import QuickSearchModal from "./QuickSearchModal";

const Styled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;

  width: 100%;
  height: 100%;
`;

const Header = styled.div`
  box-shadow: 0 0 0 1px ${colorFromTheme("surface5")};
  background-color: #f7f7f7;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  margin: auto;
  padding: 1.5rem 2rem;
  position: relative;

  max-width: var(--app-contain-width);
`;

const StyledActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-grow: 1;
`;

const StyledLogoWrapper = styled(RouterLink)`
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #dedef3;
  }
`;
const StyledLogo = styled.img`
  display: block;
  height: 50px;
`;

export const FullWidth = styled.div``;

const StyledContent = styled.div`
  padding-bottom: 5rem;

  --gutter: 2rem;

  display: grid;
  grid-template-columns:
    minmax(var(--gutter), 1fr)
    min(var(--app-contain-width), calc(100% - (var(--gutter) * 2)))
    minmax(var(--gutter), 1fr);

  > * {
    grid-column: 2;
  }

  ${FullWidth} {
    grid-column: 1 / -1;
    padding: 0 var(--gutter);
  }
`;

type AppWrapperProps = {
  children: React.ReactNode;
};

export const WindowContext = React.createContext<{
  device?: "phone" | "tablet" | "desktop";
}>({});

// todo: handle search
const AppWrapper = ({ children }: AppWrapperProps) => {
  const [state] = useAppContext();
  const switchModal = useModal(false);

  const isPhone = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const navigate = useNavigate();

  const { feedbackFormEnabled } = useFlags();
  const [isSearchModalOpen, setIsSearchModalOpen] = React.useState(false);

  return (
    <WindowContext.Provider
      value={{
        device: isPhone ? "phone" : isTablet ? "tablet" : "desktop",
      }}
    >
      <SwitchWorkspaceModal
        {...switchModal.modalProps}
        onSelect={() => {
          switchModal.setIsOpen(false);
          window.location.href = "/";
        }}
      />
      <Styled>
        <Header>
          <HeaderContent>
            <StyledLogoWrapper to="/">
              <StyledLogo src={logo} alt="TCMPC Logo" />
            </StyledLogoWrapper>

            <StyledActions>
              <HSpace size="0.5rem">
                {feedbackFormEnabled && (
                  <FeedbackButton
                    data-cy="header-feedback"
                    variant="outlined"
                    title={"Feedback"}
                  />
                )}
                <Button
                  data-cy="header-help"
                  variant="outlined"
                  title={"Help"}
                  onClick={() =>
                    window.open(
                      "https://intercom.help/go-tcmpc",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <IconQuestionCircleLine width="16" />
                </Button>
                <Button
                  data-cy="header-workspace"
                  variant="outlined"
                  title={"Workspace"}
                  onClick={() => navigate("/workspace")}
                >
                  <IconCampusLine width="16" />
                  {!isPhone && <>{state.workspaceUser?.workspace?.name}</>}
                </Button>
                <AccountButton />
                <Button
                  data-cy="header-switch-workspace"
                  title="Switch Workspace"
                  variant="outlined"
                  icon={
                    <svg viewBox="0 0 16 16" width="16">
                      <g id="Icon">
                        <path d="m14 10.75h-10c-.414 0-.75.336-.75.75s.336.75.75.75h10c.414 0 .75-.336.75-.75s-.336-.75-.75-.75z"></path>
                        <path d="m2 5.25h10c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-10c-.414 0-.75.336-.75.75s.336.75.75.75z"></path>
                        <path d="m10.97 9.53 1.969 1.97s-1.969 1.97-1.969 1.97c-.293.292-.293.768 0 1.06.292.293.768.293 1.06 0l2.5-2.5c.293-.293.293-.767 0-1.06l-2.5-2.5c-.292-.293-.768-.293-1.06 0-.293.292-.293.768 0 1.06z"></path>
                        <path d="m5.03 6.47-1.969-1.97s1.969-1.97 1.969-1.97c.293-.292.293-.768 0-1.06-.292-.293-.768-.293-1.06 0l-2.5 2.5c-.293.293-.293.767 0 1.06l2.5 2.5c.292.293.768.293 1.06 0 .293-.292.293-.768 0-1.06z"></path>
                      </g>
                    </svg>
                  }
                  onClick={() => switchModal.setIsOpen(true)}
                />
                <Button
                  data-cy="header-search"
                  variant="outlined"
                  title={"Search"}
                  onClick={() => setIsSearchModalOpen(true)}
                >
                  <svg viewBox="0 0 32 32" width="16">
                    <path
                      id="XMLID_41_"
                      d="m27.707 26.293-5.969-5.969c1.412-1.724 2.262-3.926 2.262-6.324 0-5.514-4.486-10-10-10s-10 4.486-10 10 4.486 10 10 10c2.398 0 4.6-.85 6.324-2.262l5.969 5.969c.391.391 1.023.391 1.414 0s.391-1.023 0-1.414zm-21.707-12.293c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8-8-3.589-8-8z"
                    />
                  </svg>
                  {!isPhone && <>Search</>}
                </Button>
              </HSpace>
            </StyledActions>
          </HeaderContent>
        </Header>
        <QuickSearchModal
          isSearchModalOpen={isSearchModalOpen}
          setIsSearchModalOpen={setIsSearchModalOpen}
        />
        <StyledContent>{children}</StyledContent>
      </Styled>
    </WindowContext.Provider>
  );
};

export default AppWrapper;
